import _Button from '@design-system/components/atoms/Button/_Button'
import clsx from 'clsx'
import { observer } from 'mobx-react'
import { useLocalObservable } from 'mobx-react-lite'
import { useEffect } from 'react'

import { Badge } from '@/component/atoms/Badge'
import Name from '@/component/atoms/Name'
import { Dropdown, DropdownItem } from '@/component/molecules/Dropdown'
import { Radio } from '@/component/molecules/Radio'
import AssignGroupStudentModal from '@/contentComponents/modal/AssignGroupStudentModal/AssignGroupStudentModal'
import type { WorksheetExportType } from '@/contentComponents/modal/Download/type'
import { isAllStudentExportType } from '@/contentComponents/modal/Download/worksheetExportType'
import commonRepo from '@/repository/common.repo'
import modalService from '@/service/modal.service'
import { worksheetPrintService } from '@/service/print'
import { stringUtils } from '@/utils/String'

import S from './StudentNameBox.style'

type Props = {
  type?: WorksheetExportType
}

const StudentNameBox = ({ type }: Props) => {
  const isWeizmann = type === 'WEIZMANN'
  const uniqueStudentCount = new Set(
    worksheetPrintService.printWorksheetList?.flatMap(({ assignedStudentWorksheetList }) =>
      assignedStudentWorksheetList?.flatMap(({ studentId }) => studentId),
    ),
  ).size

  const store = useLocalObservable(() => ({
    selectedWorksheetId: undefined as number | undefined,
    onSelectWorksheet: (worksheetId: number) => {
      store.selectedWorksheetId = worksheetId
    },
    get selectedWorksheet() {
      if (!store.selectedWorksheetId) return undefined

      return worksheetPrintService.printWorksheetMap.get(store.selectedWorksheetId)
    },
  }))

  useEffect(() => {
    const targetId = worksheetPrintService.printWorksheetList?.[0]?.id
    if (!store.selectedWorksheetId && targetId) {
      store.selectedWorksheetId = targetId
    }
  }, [worksheetPrintService.printWorksheetList, store])

  return (
    <div css={S.StudentNameBox}>
      <div css={S.StudentNameHeader}>
        <div className="title">
          <span>
            {isWeizmann ? `${commonRepo.agencyNameInKor} 및 학생 정보` : '학생 이름 표시'}
          </span>
          {worksheetPrintService.uiStore.visibleStudentOption === 'VISIBLE' && (
            <span className="count">(총 학생수 {uniqueStudentCount}명)</span>
          )}
        </div>
        <div className="radio-group">
          <Radio.Group
            value={worksheetPrintService.uiStore.visibleStudentOption}
            onChange={(event) =>
              worksheetPrintService.uiStore.update({
                visibleStudentOption: event.target.value as 'NONE' | 'VISIBLE',
              })
            }
          >
            <Radio value="NONE">표시 안함</Radio>
            <Radio value="VISIBLE">
              {isWeizmann ? `${commonRepo.agencyNameInKor} 및 학생 정보` : '학생 이름 표시'}
            </Radio>
          </Radio.Group>
        </div>
      </div>

      {worksheetPrintService.uiStore.visibleStudentOption === 'VISIBLE' && (
        <>
          <div css={S.StudentNameDivider} />

          <div css={S.StudentNameSelectRow}>
            <Dropdown
              className={clsx(
                'dropdown',
                worksheetPrintService.isStudentListSelectionRequired && 'error',
              )}
              width={320}
              height={40}
              value={store.selectedWorksheetId || ''}
              onChange={(value: number) => {
                store.onSelectWorksheet(value)
              }}
            >
              {worksheetPrintService.printWorksheetList?.map(
                ({ title, id, assignedStudentWorksheetList }) => (
                  <DropdownItem key={id} value={id}>
                    <div css={S.DropdownItemChildren} className="dropdown-item-children">
                      <p>{title}</p>
                      {assignedStudentWorksheetList?.length === 0 && (
                        <Badge
                          className="dropdown-item-badge"
                          size="xs"
                          color="var(--Colors-Indigo-50)"
                          backgroundColor="var(--Colors-Indigo-500)"
                          radius="100"
                        >
                          학생 선택 필요
                        </Badge>
                      )}
                    </div>
                  </DropdownItem>
                ),
              )}
            </Dropdown>
            <_Button
              theme="outline"
              width={120}
              size="m"
              onClick={() => {
                modalService.openModal(
                  <AssignGroupStudentModal
                    title="학생 선택"
                    className="worksheet-print"
                    fetchAllStudents={isAllStudentExportType(type)}
                    assignedLists={[
                      store.selectedWorksheet?.assignedStudentWorksheetList?.map(
                        ({ studentId, studentName, studentWorksheetId }) => ({
                          studentId,
                          studentDataRelationId: studentWorksheetId,
                          studentName,
                        }),
                      ) || [],
                    ]}
                    warnDeleteAssigned={false}
                    onComplete={(data) => {
                      const { selectedWorksheetId } = store
                      if (!selectedWorksheetId) return

                      const updatedAssignedList = data.map(({ studentId, studentName }) => ({
                        studentId,
                        studentName,
                        studentWorksheetId: selectedWorksheetId,
                      }))

                      worksheetPrintService.printWorksheetMap.set(selectedWorksheetId, {
                        title: store.selectedWorksheet?.title || '',
                        assignedStudentWorksheetList: updatedAssignedList,
                      })
                    }}
                  />,
                )
              }}
            >
              <Name maxWidth={100} textAlign="center" smallSizeLength={7}>
                {stringUtils.makeAssignCountText(
                  store.selectedWorksheet?.assignedStudentWorksheetList?.[0]?.studentName,
                  store.selectedWorksheet?.assignedStudentWorksheetList?.length,
                  '선택하기',
                )}
              </Name>
            </_Button>
          </div>
          {worksheetPrintService.isStudentListSelectionRequired && (
            <p className="error-message">모든 학습지에 학생을 선택해주세요.</p>
          )}
          <div css={S.StudentNameDesc}>
            해당 학습지를 출력한 학생이 자동 표시됩니다.
            <br />
            다른 학생에게도 학습지를 주고 싶다면 이름을 눌러 변경하거나 추가할 수 있습니다.
          </div>
        </>
      )}
    </div>
  )
}

export default observer(StudentNameBox)
